import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { Avatar, Card, Chip, Divider, Grid, IconButton, InputAdornment, LinearProgress, MenuItem, Skeleton, Tooltip } from "@mui/material";
// Büyük formda her tuşta tüm formun yeniden çizilmesini önleyen tamponlu TextField (yazma gecikmesi)
import TextField from "components/BufferedTextField";

import {
    AccountBalanceWallet,
    Download,
    FilterAlt,
    Paid,
    ReceiptLong,
    Refresh,
    RestartAlt,
    Search,
    Visibility,
    WarningAmber,
    DirectionsBoat,
    Flight,
    LocalShipping,
    Inventory2,
} from "@mui/icons-material";

import FinanceStatusChip from "../components/FinanceStatusChip";
import { formatDate } from "../utils/formatter";
import {
    COLORS,
    cardSx,
    FinanceCard,
    FinanceSectionHeader,
    FinanceKpiTile,
    FinanceHeroStat,
    SPACE,
    FinanceDetailsSection,
} from "../shared/FinanceUiKit";

import {
    financeText,
    getFinanceLanguage,
    saveFinanceLanguage,
} from "finance-language";
import { appAlert } from "components/AppDialog";
// Koyu hero bandı için (yedekten geri yüklendi)
import { WorkspacePremium } from "@mui/icons-material";
import { CheckCircle } from "@mui/icons-material";
import { CalendarMonth } from "@mui/icons-material";

const API_URL = process.env.REACT_APP_API_URL;

const getValue = (row, camel, snake, fallback = "") =>
    row?.[camel] ?? row?.[snake] ?? fallback;

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const firstDefinedNumber = (...values) => {
    const value = values.find(
        (item) => item !== undefined && item !== null && item !== "",
    );

    return toNumber(value);
};

const normalizeCurrencyCode = (currency) => {
    const code = String(currency || "EUR")
        .trim()
        .toUpperCase();

    if (code === "EURO") return "EUR";
    if (code === "TL") return "TRY";

    return code || "EUR";
};

const getCurrency = (item) =>
    normalizeCurrencyCode(
        item?.costCurrency ||
        item?.cost_currency ||
        item?.currency ||
        item?.invoiceCurrency ||
        item?.invoice_currency ||
        item?.totalCostCurrency ||
        item?.total_cost_currency ||
        item?.financialSummary?.costCurrency ||
        item?.financialSummary?.totalCurrency ||
        item?.financial_summary?.total_currency ||
        item?.freightCharges?.totalCurrency ||
        item?.freight_charges?.total_currency ||
        "EUR",
    );

const extractRows = (data) => {
    const candidates = [
        data,
        data?.data,
        data?.payables,
        data?.returnInvoices,
        data?.return_invoices,
        data?.supplierInvoices,
        data?.supplier_invoices,
        data?.invoices,
        data?.results,
        data?.records,
        data?.data?.payables,
        data?.data?.returnInvoices,
        data?.data?.return_invoices,
        data?.data?.supplierInvoices,
        data?.data?.supplier_invoices,
        data?.data?.invoices,
        data?.data?.results,
        data?.data?.records,
    ];

    return candidates.find(Array.isArray) || [];
};

const getEntityId = (value) => {
    if (!value) return "";
    if (typeof value === "object") {
        return getEntityId(value?._id || value?.id || value?.$oid);
    }

    return String(value).trim();
};

const getGrossPayable = (item) =>
    firstDefinedNumber(
        item?.grossPayableAmount,
        item?.gross_payable_amount,
        item?.netPayableAmount,
        item?.net_payable_amount,
        item?.totalAmount,
        item?.total_amount,
    );

const getReturnStatus = (item) =>
    String(item?.returnStatus || item?.return_status || "Draft").trim();

const getOriginalInvoiceId = (item) =>
    getEntityId(
        item?.originalInvoiceId ||
        item?.original_invoice_id ||
        item?.originalInvoice?._id ||
        item?.originalInvoice?.id,
    );

const getOriginalInvoiceNo = (item) =>
    String(
        item?.originalInvoiceNumber ||
        item?.original_invoice_number ||
        item?.originalInvoiceNo ||
        item?.original_invoice_no ||
        item?.originalInvoice?.invoiceNo ||
        item?.originalInvoice?.invoice_no ||
        "",
    ).trim();

const getReturnInvoiceNo = (item) =>
    String(
        item?.returnInvoiceNo ||
        item?.return_invoice_no ||
        item?.invoiceNo ||
        item?.invoice_no ||
        item?._id ||
        "N/A",
    ).trim();

const getReturnInvoiceAmount = (item) =>
    Math.abs(
        firstDefinedNumber(
            item?.returnedAmount,
            item?.returned_amount,
            item?.returnAmount,
            item?.return_amount,
            item?.returnedTotalWithVat,
            item?.returned_total_with_vat,
            item?.totalWithVat,
            item?.total_with_vat,
            item?.totalAmount,
            item?.total_amount,
            item?.invoiceSalesAmount,
            item?.amount,
        ),
    );

// The currency a record EXPLICITLY declares, or null when it declares none.
// Unlike `getCurrency` this never guesses "EUR": a return invoice that carries
// no currency of its own inherits the currency of the payable it offsets, and
// treating that silent default as a real "EUR" would convert a TRY amount as
// though it were EUR -- far worse than not converting at all.
const getExplicitCurrency = (item) => {
    const raw =
        item?.costCurrency ||
        item?.cost_currency ||
        item?.currency ||
        item?.invoiceCurrency ||
        item?.invoice_currency ||
        item?.totalCostCurrency ||
        item?.total_cost_currency ||
        item?.returnCurrency ||
        item?.return_currency;

    return raw ? normalizeCurrencyCode(raw) : null;
};

// Sums return-invoice amounts into `payableCurrency`.
//
// `convert(amount, fromCurrency, toCurrency)` is optional. Return invoices
// carry their OWN currency, which need not match the payable they offset --
// subtracting a TRY credit note straight off a EUR supplier invoice is the
// same bug class as computing profit from EUR revenue and USD cost. Passing a
// converter normalizes every return into the payable's currency first.
// Without one (or when a rate is missing, or when the return declares no
// currency and therefore inherits the payable's) this falls back to the raw
// amount, which is what the old code always did.
const sumReturnAmounts = (returnInvoices, payableCurrency, convert) =>
    returnInvoices.reduce((sum, returnInvoice) => {
        const amount = getReturnInvoiceAmount(returnInvoice);
        const currency = getExplicitCurrency(returnInvoice);

        if (typeof convert !== "function" || !currency || currency === payableCurrency) {
            return sum + amount;
        }

        const converted = convert(amount, currency, payableCurrency);

        return sum + (converted === null || converted === undefined ? amount : converted);
    }, 0);

const getLinkedReturnInvoices = (item) =>
    [
        item?.returnInvoices,
        item?.return_invoices,
        item?.linkedReturnInvoices,
        item?.linked_return_invoices,
    ].find(Array.isArray) || [];

const getReturnDate = (item) => {
    const raw =
        item?.returnDate ||
        item?.return_date ||
        item?.invoiceDate ||
        item?.invoice_date ||
        item?.createdAt ||
        item?.created_at ||
        item?.updatedAt ||
        item?.updated_at;
    const date = raw ? new Date(raw) : null;

    return date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
};

// `convert` is threaded through so `issuedAmount`/`reservedAmount` come back in
// the payable's own currency (see sumReturnAmounts) -- everything downstream
// (net payable, open amount, supplier credit, the Full/Partial Return
// comparison against the gross amount) subtracts or compares them against
// amounts in that currency.
const getReturnSummary = (item, convert) => {
    const payableCurrency = getCurrency(item);
    const allReturns = getLinkedReturnInvoices(item);
    const activeReturns = allReturns.filter(
        (returnInvoice) =>
            getReturnStatus(returnInvoice).toLowerCase() !== "cancelled",
    );
    const issuedReturns = activeReturns.filter(
        (returnInvoice) =>
            getReturnStatus(returnInvoice).toLowerCase() === "issued",
    );
    const latestActiveReturn =
        [...activeReturns].sort((a, b) => getReturnDate(b) - getReturnDate(a))[0] ||
        null;
    const latestReturnInvoice =
        [...allReturns].sort((a, b) => getReturnDate(b) - getReturnDate(a))[0] ||
        null;
    const issuedAmount = sumReturnAmounts(issuedReturns, payableCurrency, convert);
    const reservedAmount = sumReturnAmounts(
        activeReturns,
        payableCurrency,
        convert,
    );
    const originalAmount = Math.abs(getGrossPayable(item));
    const latestStatus = latestActiveReturn
        ? getReturnStatus(latestActiveReturn).toLowerCase()
        : "";

    let status = "No Return";
    if (latestStatus === "draft") status = "Return Draft";
    else if (latestStatus === "approved") status = "Return Approved";
    else if (
        issuedAmount > 0 &&
        originalAmount > 0 &&
        issuedAmount >= originalAmount - 0.01
    ) {
        status = "Full Return";
    } else if (issuedAmount > 0) status = "Partial Return";
    else if (reservedAmount > 0) status = "Return Pending";
    else if (allReturns.length > 0) status = "Return Cancelled";

    return {
        status,
        issuedAmount,
        reservedAmount,
        count: allReturns.length,
        latestReturnInvoice,
    };
};

// Every balance below is expressed in the payable's OWN currency; `convert`
// only normalizes the return-invoice leg before subtracting it (see
// getReturnSummary). Omitting it keeps the previous raw behaviour.
const getNetPayable = (item, convert) =>
    Math.max(
        getGrossPayable(item) - getReturnSummary(item, convert).issuedAmount,
        0,
    );

const getPaidAmount = (item) =>
    firstDefinedNumber(item?.paidAmount, item?.paid_amount);

const getOpenAmount = (item, convert) =>
    Math.max(getNetPayable(item, convert) - getPaidAmount(item), 0);

const getSupplierCreditAmount = (item, convert) =>
    Math.max(getPaidAmount(item) - getNetPayable(item, convert), 0);

const getBuyingRate = (item) =>
    firstDefinedNumber(
        item?.buyingRate,
        item?.buying_rate,
        item?.financialSummary?.buyingRate,
        item?.financial_summary?.buying_rate,
        item?.freightCharges?.buyingRate,
        item?.freight_charges?.buying_rate,
    );

const getOperationalCost = (item) => {
    const summary = item?.financialSummary || item?.financial_summary || {};
    const freightCharges = item?.freightCharges || item?.freight_charges || {};

    const explicitOperationalCost = firstDefinedNumber(
        item?.operationalCost,
        item?.operational_cost,
        summary?.operationalCost,
        summary?.operational_cost,
    );

    if (explicitOperationalCost > 0) return explicitOperationalCost;

    const roadCost =
        firstDefinedNumber(summary?.shipmentCostTotal) +
        firstDefinedNumber(summary?.convertedLinehaulCost) +
        firstDefinedNumber(summary?.convertedHandlingCost);

    const oceanCost =
        firstDefinedNumber(summary?.pickupTotal) +
        firstDefinedNumber(summary?.othcTotal) +
        firstDefinedNumber(summary?.oceanFreightTotal) +
        firstDefinedNumber(summary?.dthcTotal) +
        firstDefinedNumber(summary?.handlingTotal) +
        firstDefinedNumber(summary?.deliveryTotal);

    const airCost =
        firstDefinedNumber(summary?.pickupTotal) +
        firstDefinedNumber(summary?.othcTotal) +
        firstDefinedNumber(summary?.airFreightTotal) +
        firstDefinedNumber(summary?.dthcTotal) +
        firstDefinedNumber(summary?.handlingTotal) +
        firstDefinedNumber(summary?.deliveryTotal);

    const freightChargeCost =
        firstDefinedNumber(freightCharges?.pickupTotal) +
        firstDefinedNumber(freightCharges?.othc?.total) +
        firstDefinedNumber(freightCharges?.oceanFreight?.total) +
        firstDefinedNumber(freightCharges?.airFreight?.total) +
        firstDefinedNumber(freightCharges?.dthc?.total) +
        firstDefinedNumber(freightCharges?.handling?.total) +
        firstDefinedNumber(freightCharges?.deliveryTotal);

    if (roadCost > 0) return roadCost;
    if (oceanCost > 0) return oceanCost;
    if (airCost > 0) return airCost;
    if (freightChargeCost > 0) return freightChargeCost;

    return 0;
};

const getMasterTotalCost = (item) => {
    const explicitTotalCost = firstDefinedNumber(
        item?.actualCostTotal,
        item?.actual_cost_total,
        item?.totalCost,
        item?.total_cost,
        item?.financialSummary?.totalCost,
        item?.financial_summary?.total_cost,
    );

    if (explicitTotalCost > 0) {
        // Backend total fields are final totals. Adding buyingRate again here
        // inflates the cost when the same Finance Job has multiple payables.
        return explicitTotalCost;
    }

    return getOperationalCost(item) + getBuyingRate(item);
};

const isIncludedInMasterCost = (item) =>
    item?.includedInMasterCost === true || item?.included_in_master_cost === true;

const getNetMasterCost = (item, convert) =>
    Math.max(
        getMasterTotalCost(item) -
        (isIncludedInMasterCost(item)
            ? getReturnSummary(item, convert).issuedAmount
            : 0),
        0,
    );

const getAdditionalPayableCost = (item, convert) =>
    isIncludedInMasterCost(item) ? 0 : getNetPayable(item, convert);

const getFinanceJobKey = (item) => {
    const financeJobId = getEntityId(
        item?.financeJobId ||
        item?.finance_job_id ||
        item?.jobFinanceId ||
        item?.job_finance_id ||
        item?.financeJob?._id ||
        item?.finance_job?._id ||
        item?.job?._id,
    );

    if (financeJobId) return `JOB-${financeJobId}`;

    const shipmentNo = String(getShipmentNo(item) || "").trim();
    if (shipmentNo && shipmentNo !== "N/A") return `SHIPMENT-${shipmentNo}`;

    // Unlinked manual invoices must remain independent records.
    return `PAYABLE-${getEntityId(item?._id || item?.id) || getInvoiceNo(item)}`;
};

const getDueDays = (dueDate, paymentStatus) => {
    if (!dueDate) return null;

    const status = String(paymentStatus || "").toLowerCase();

    if (
        status.includes("paid") ||
        status.includes("returned") ||
        status.includes("credit due")
    )
        return 0;

    const today = new Date();
    const due = new Date(dueDate);

    if (Number.isNaN(due.getTime())) return null;

    const diff = due.getTime() - today.getTime();
    return Math.ceil(diff / 86400000);
};

// Paid / returned / credit-due payables have nothing left to pay, so they are
// not "due today" (getDueDays reports 0 for them) -- they are simply closed.
const isClosedPaymentStatus = (paymentStatus) => {
    const status = String(paymentStatus || "").toLowerCase();

    return (
        status.includes("paid") ||
        status.includes("returned") ||
        status.includes("credit due")
    );
};

const getDueBucket = (dueDate, paymentStatus) => {
    if (isClosedPaymentStatus(paymentStatus)) return "Closed";

    const days = getDueDays(dueDate, paymentStatus);

    if (days === null) return "No Due Date";
    if (days < 0) return "Overdue";
    if (days === 0) return "Due Today";
    if (days <= 7) return "This Week";
    if (days <= 30) return "This Month";

    return "Later";
};

const getPaymentStatus = (item) =>
    getValue(item, "paymentStatus", "payment_status", "Not Due");

const getEffectivePaymentStatus = (item, convert) => {
    if (getSupplierCreditAmount(item, convert) > 0.01) return "Credit Due";
    if (
        getNetPayable(item, convert) <= 0.01 &&
        getReturnSummary(item, convert).issuedAmount > 0
    ) {
        return "Fully Returned";
    }
    if (
        getOpenAmount(item, convert) <= 0.01 &&
        getNetPayable(item, convert) > 0
    )
        return "Paid";
    if (getPaidAmount(item) > 0) return "Partial";

    return getPaymentStatus(item);
};

const getApprovalStatus = (item) =>
    getValue(item, "approvalStatus", "approval_status", "Pending");

const getShipmentNo = (item) =>
    getValue(item, "jobNo", "job_no") ||
    item?.shipmentNo ||
    item?.shipment_no ||
    item?.invoiceNo ||
    item?.invoice_no ||
    "N/A";

const getSupplierName = (item) =>
    getValue(item, "supplierName", "supplier_name", "N/A");

const getCustomerName = (item) =>
    getValue(item, "customerName", "customer_name", item?.customer || "N/A");

const getDateValue = (item) => {
    const raw =
        item?.invoiceDate ||
        item?.invoice_date ||
        item?.supplierInvoiceDate ||
        item?.supplier_invoice_date ||
        item?.createdAt ||
        item?.created_at ||
        item?.updatedAt ||
        item?.updated_at ||
        item?.dueDate ||
        item?.due_date;

    const date = raw ? new Date(raw) : null;
    return date && !Number.isNaN(date.getTime()) ? date : null;
};

const normalizeCountry = (value) =>
    String(value || "")
        .trim()
        .toLocaleUpperCase("tr-TR")
        .replace(/İ/g, "I")
        .replace(/Ü/g, "U");
const normalizeLookupKey = (value) => {
    if (!value) return "";
    if (typeof value === "object")
        return normalizeLookupKey(
            value?._id ||
            value?.id ||
            value?.$oid ||
            value?.nameKey ||
            value?.name ||
            value?.companyName,
        );
    return String(value)
        .trim()
        .toLocaleUpperCase("tr-TR")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^A-Z0-9]+/g, " ")
        .trim();
};
const getDirectCountries = (item) =>
    [
        item,
        item?.customer,
        item?.serviceProvider,
        item?.supplier,
        item?.globalAgent,
        item?.selectedBookingAgent,
        item?.selectedOriginAgent,
        item?.selectedDestinationAgent,
        item?.selectedNotifyAgent,
    ]
        .filter((value) => value && typeof value === "object")
        .flatMap((value) => [
            value?.customerCountry,
            value?.serviceProviderCountry,
            value?.globalAgentCountry,
            ...(Array.isArray(value?.servicesCountry) ? value.servicesCountry : []),
        ])
        .filter(Boolean);
const getPartyKeys = (item) =>
    [
        item?.customerId,
        item?.customer,
        item?.customerName,
        item?.customer_name,
        item?.serviceProviderId,
        item?.serviceProvider,
        item?.serviceProviderName,
        item?.supplierId,
        item?.supplier,
        item?.supplierName,
        item?.supplier_name,
        item?.globalAgentId,
        item?.globalAgent,
        item?.globalAgentName,
        item?.selectedBookingAgent,
        item?.selectedOriginAgent,
        item?.selectedDestinationAgent,
        item?.selectedNotifyAgent,
    ]
        .map(normalizeLookupKey)
        .filter(Boolean);
const getGeographicScope = (item, lookup = new Map()) => {
    const countries = [
        ...getDirectCountries(item),
        ...getPartyKeys(item).flatMap((key) => lookup.get(key) || []),
    ];
    return countries.some((country) =>
        ["TURKEY", "TURKIYE"].includes(normalizeCountry(country)),
    )
        ? "DOMESTIC"
        : "INTERNATIONAL";
};
const getGeographicLabel = (item, lookup) =>
    getGeographicScope(item, lookup) === "DOMESTIC"
        ? "Domestic"
        : "International";

const getInvoiceNo = (item) => getValue(item, "invoiceNo", "invoice_no", "N/A");

const formatCompact = (value, currency = "EUR") =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        notation: "compact",
        maximumFractionDigits: 1,
    }).format(toNumber(value));

const formatNumber = (value) =>
    new Intl.NumberFormat("en-US", {
        maximumFractionDigits: 0,
    }).format(toNumber(value));

// Currencies offered by the "Display Currency" selector -- the currency every
// AGGREGATED total (KPI cards, transport/shipment-type breakdowns, payment
// schedule, summary chips, CSV export) is converted into before it is shown.
// Not to be confused with the record-level `currencyFilter`, which only
// decides which payable records are listed.
const CURRENCY_OPTIONS = ["EUR", "USD", "TRY", "GBP"];

const normalizeText = (value) =>
    String(value || "")
        .trim()
        .toLowerCase();

const getInvoiceOrigin = (item) =>
    normalizeText(item?.invoiceOrigin || item?.invoice_origin) === "manual"
        ? "manual"
        : "tms";

const getTransportMode = (item) => {
    if (getInvoiceOrigin(item) === "manual") return "Manual";

    const source = [
        item?.sourceType,
        item?.source_type,
        item?.transportMode,
        item?.transport_mode,
        item?.shipmentMode,
        item?.shipment_mode,
        item?.productType,
        item?.product_type,
        item?.serviceType,
        item?.service_type,
        item?.module,
        item?.source,
        item?.jobType,
        item?.job_type,
        getShipmentNo(item),
    ]
        .map(normalizeText)
        .join(" ");

    if (/air|ambl|ahfl|airport|flight/.test(source)) return "Air";
    if (/road|land|truck|ftl|ltl|rmbl|rhfl|intermodal/.test(source))
        return "Road";
    return "Ocean";
};

const getShipmentType = (item) => {
    const source = [
        item?.shipmentType,
        item?.shipment_type,
        item?.transportType,
        item?.transport_type,
        item?.oceanTransportType,
        item?.ocean_transport_type,
        item?.airTransportType,
        item?.air_transport_type,
        item?.serviceType,
        item?.service_type,
        item?.productType,
        item?.product_type,
    ]
        .map(normalizeText)
        .join(" ");

    if (/fcl/.test(source)) return "FCL";
    if (/lcl/.test(source)) return "LCL";
    if (/ftl/.test(source)) return "FTL";
    if (/ltl/.test(source)) return "LTL";
    if (/air|express/.test(source)) return "Air Freight";
    if (/intermodal/.test(source)) return "Intermodal";
    return getTransportMode(item) === "Ocean"
        ? "Ocean General"
        : `${getTransportMode(item)} General`;
};

const MODE_CONFIG = {
    Ocean: {
        color: COLORS.blue,
        bg: COLORS.blueLight,
        border: "#B2DDFF",
        icon: DirectionsBoat,
    },
    Road: {
        color: COLORS.amber,
        bg: COLORS.amberLight,
        border: "#FEDF89",
        icon: LocalShipping,
    },
    Air: {
        color: COLORS.purple,
        bg: COLORS.purpleLight,
        border: "#D9D6FE",
        icon: Flight,
    },
    Manual: {
        color: COLORS.subtext,
        bg: "#F2F4F7",
        border: COLORS.border,
        icon: ReceiptLong,
    },
};

const RETURN_STATUS_STYLES = {
    "No Return": { color: "#475467", background: "#F2F4F7", border: "#D0D5DD" },
    "Return Draft": {
        color: "#B54708",
        background: "#FFFAEB",
        border: "#FEDF89",
    },
    "Return Approved": {
        color: "#175CD3",
        background: "#EFF8FF",
        border: "#B2DDFF",
    },
    "Partial Return": {
        color: "#6941C6",
        background: "#F4F3FF",
        border: "#D9D6FE",
    },
    "Full Return": { color: "#B42318", background: "#FEF3F2", border: "#FECDCA" },
    "Return Pending": {
        color: "#B54708",
        background: "#FFFAEB",
        border: "#FEDF89",
    },
    "Return Cancelled": {
        color: "#475467",
        background: "#F2F4F7",
        border: "#D0D5DD",
    },
};

const TransportModeCard = ({ mode, data, currency = "EUR" }) => {
    const config = MODE_CONFIG[mode] || MODE_CONFIG.Manual;
    const Icon = config.icon;
    return (
        <Card
            sx={{ ...cardSx, height: "100%", borderTop: `4px solid ${config.color}` }}
        >
            <MDBox p={2.3}>
                <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                >
                    <MDBox>
                        <MDTypography
                            variant="caption"
                            fontWeight="bold"
                            sx={{ color: COLORS.subtext, letterSpacing: ".08em" }}
                        >
                            {`${financeText(mode).toUpperCase()} ${financeText("PAYABLES")}`}
                        </MDTypography>
                        <MDTypography
                            variant="h4"
                            fontWeight="bold"
                            mt={0.8}
                            sx={{ color: COLORS.text }}
                        >
                            {formatCompact(data.open, currency)}
                        </MDTypography>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {data.count} {financeText("records")} · {data.paymentRate.toFixed(1)}% {financeText("paid")}
                        </MDTypography>
                    </MDBox>
                    <MDBox
                        width={44}
                        height={44}
                        borderRadius="8px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            color: config.color,
                            background: config.bg,
                            border: `1px solid ${config.border}`,
                        }}
                    >
                        <Icon />
                    </MDBox>
                </MDBox>
                <Divider sx={{ my: 1.6 }} />
                <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
                    <MDBox>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {financeText("Total")}
                        </MDTypography>
                        <MDTypography
                            variant="button"
                            display="block"
                            fontWeight="bold"
                            sx={{ color: COLORS.text }}
                        >
                            {formatCompact(data.total, currency)}
                        </MDTypography>
                    </MDBox>
                    <MDBox>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {financeText("Overdue")}
                        </MDTypography>
                        <MDTypography
                            variant="button"
                            display="block"
                            fontWeight="bold"
                            sx={{ color: data.overdue > 0 ? COLORS.red : COLORS.green }}
                        >
                            {formatCompact(data.overdue, currency)}
                        </MDTypography>
                    </MDBox>
                </MDBox>
            </MDBox>
        </Card>
    );
};

const ModeComparison = ({ summary, currency = "EUR" }) => {
    const max = Math.max(...Object.values(summary).map((item) => item.open), 1);
    return (
        <Card sx={{ ...cardSx, height: "100%" }}>
            <MDBox p={2.5}>
                <MDTypography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ color: COLORS.text }}
                >
                    {financeText("Payables by Transport Mode")}
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                    {financeText("Open supplier exposure by operation type.")}
                </MDTypography>
                <MDBox mt={2.2}>
                    {Object.entries(summary).map(([mode, data]) => {
                        const config = MODE_CONFIG[mode] || MODE_CONFIG.Manual;
                        const width = (data.open / max) * 100;
                        return (
                            <MDBox key={financeText(mode)} mb={1.8}>
                                <MDBox display="flex" justifyContent="space-between" mb={0.6}>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        sx={{ color: COLORS.text }}
                                    >
                                        {financeText(mode)}
                                    </MDTypography>
                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{ color: config.color }}
                                    >
                                        {formatCompact(data.open, currency)}
                                    </MDTypography>
                                </MDBox>
                                <MDBox
                                    height={9}
                                    borderRadius="999px"
                                    sx={{ background: "#F2F4F7", overflow: "hidden" }}
                                >
                                    <MDBox
                                        height="100%"
                                        borderRadius="999px"
                                        sx={{
                                            width: `${width}%`,
                                            minWidth: data.open > 0 ? 8 : 0,
                                            background: config.color,
                                        }}
                                    />
                                </MDBox>
                            </MDBox>
                        );
                    })}
                </MDBox>
            </MDBox>
        </Card>
    );
};

const Payables = () => {
    const navigate = useNavigate();

    const [financeLanguage, setFinanceLanguageState] = useState(getFinanceLanguage);

    const changeFinanceLanguage = (event) => {
        const nextLanguage = event.target.value === "tr" ? "tr" : "en";
        saveFinanceLanguage(nextLanguage);
        setFinanceLanguageState(nextLanguage);
    };

    const [payables, setPayables] = useState([]);
    const [businessPartners, setBusinessPartners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(new Date());

    const [search, setSearch] = useState("");
    const [paymentStatus, setPaymentStatus] = useState("all");
    const [approvalStatus, setApprovalStatus] = useState("all");
    const [dueBucket, setDueBucket] = useState("all");
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const todayValue = `${currentYear}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;
    const [startDate, setStartDate] = useState(`${currentYear}-01-01`);
    const [endDate, setEndDate] = useState(todayValue);
    const [selectedParty, setSelectedParty] = useState(null);
    const [transportMode, setTransportMode] = useState("all");
    const [invoiceOrigin, setInvoiceOrigin] = useState("all");
    const [currencyFilter, setCurrencyFilter] = useState("all");
    const [geographicScope, setGeographicScope] = useState("all");
    const [returnStatus, setReturnStatus] = useState("all");
    const [exchangeRates, setExchangeRates] = useState(null);

    // Display currency for AGGREGATED totals only. This is a genuinely separate
    // control from `currencyFilter` above: `currencyFilter` decides WHICH
    // RECORDS are listed (matched against each record's own currency), while
    // `selectedCurrency` decides WHAT CURRENCY the blended totals are rendered
    // in. They used to be the same thing -- the reporting currency was derived
    // from `currencyFilter` -- so narrowing the table to USD records also
    // silently relabeled every KPI/chart/CSV total as USD, and there was no way
    // to view mixed-currency totals in a currency of your choosing.
    const [selectedCurrency, setSelectedCurrency] = useState("EUR");

    const token = localStorage.getItem("token");

    const fetchWithAuth = useCallback(
        async (path) => {
            const response = await fetch(`${API_URL}${path}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) throw new Error(`${path} fetch failed`);
            const data = await response.json();
            return extractRows(data);
        },
        [token],
    );

    const fetchPayables = useCallback(async () => {
        try {
            setLoading(true);

            const [payablesResult, manualResult, returnInvoicesResult] =
                await Promise.allSettled([
                    fetchWithAuth("/finance/payables"),
                    fetchWithAuth(
                        "/finance/manual-invoices?invoiceType=purchase&limit=200",
                    ),
                    fetchWithAuth("/finance/return-invoices?invoiceType=purchase"),
                ]);

            if (payablesResult.status === "rejected") {
                throw payablesResult.reason;
            }

            const tmsPayables = payablesResult.value.map((item) => ({
                ...item,
                invoiceOrigin: item?.invoiceOrigin || item?.invoice_origin || "tms",
            }));
            const manualPayables =
                manualResult.status === "fulfilled"
                    ? manualResult.value
                        .filter(
                            (item) =>
                                normalizeText(item?.invoiceType || item?.invoice_type) ===
                                "purchase",
                        )
                        .map((item) => ({
                            ...item,
                            invoiceOrigin: "manual",
                        }))
                    : [];

            if (manualResult.status === "rejected") {
                console.warn(
                    "Manual purchase invoices could not be loaded:",
                    manualResult.reason,
                );
            }
            if (returnInvoicesResult.status === "rejected") {
                console.warn(
                    "Purchase return invoices could not be loaded:",
                    returnInvoicesResult.reason,
                );
            }

            const unique = new Map();
            [...manualPayables, ...tmsPayables].forEach((item) => {
                const key = String(
                    item?._id ||
                    item?.id ||
                    `${getInvoiceOrigin(item)}:${getInvoiceNo(item)}`,
                );
                unique.set(key, item);
            });

            const returnInvoices =
                returnInvoicesResult.status === "fulfilled"
                    ? returnInvoicesResult.value
                    : [];
            const uniqueReturnInvoices = Array.from(
                new Map(
                    returnInvoices.map((returnInvoice, index) => [
                        getEntityId(returnInvoice?._id || returnInvoice?.id) ||
                        `return-${index}`,
                        returnInvoice,
                    ]),
                ).values(),
            );
            const returnsByOriginalId = new Map();
            const returnsByOriginalNo = new Map();

            uniqueReturnInvoices.forEach((returnInvoice) => {
                const originalInvoiceId = getOriginalInvoiceId(returnInvoice);
                const originalInvoiceNo = normalizeLookupKey(
                    getOriginalInvoiceNo(returnInvoice),
                );

                if (originalInvoiceId) {
                    returnsByOriginalId.set(originalInvoiceId, [
                        ...(returnsByOriginalId.get(originalInvoiceId) || []),
                        returnInvoice,
                    ]);
                }
                if (originalInvoiceNo) {
                    returnsByOriginalNo.set(originalInvoiceNo, [
                        ...(returnsByOriginalNo.get(originalInvoiceNo) || []),
                        returnInvoice,
                    ]);
                }
            });

            const enrichedPayables = Array.from(unique.values()).map((item) => {
                const identityKeys = [
                    item?._id,
                    item?.id,
                    item?.invoiceId,
                    item?.invoice_id,
                    item?.supplierInvoiceId,
                    item?.supplier_invoice_id,
                    item?.manualInvoiceId,
                    item?.manual_invoice_id,
                ]
                    .map(getEntityId)
                    .filter(Boolean);
                const invoiceNoKey = normalizeLookupKey(getInvoiceNo(item));
                const linkedReturns = [
                    ...getLinkedReturnInvoices(item),
                    ...identityKeys.flatMap(
                        (identityKey) => returnsByOriginalId.get(identityKey) || [],
                    ),
                    ...(returnsByOriginalNo.get(invoiceNoKey) || []),
                ];
                const uniqueLinkedReturns = Array.from(
                    new Map(
                        linkedReturns.map((returnInvoice, index) => [
                            getEntityId(returnInvoice?._id || returnInvoice?.id) ||
                            `${invoiceNoKey}-return-${index}`,
                            returnInvoice,
                        ]),
                    ).values(),
                );

                return {
                    ...item,
                    returnInvoices: uniqueLinkedReturns,
                };
            });

            setPayables(enrichedPayables);

            setLastUpdated(new Date());
        } catch (error) {
            console.error("Payables fetch error:", error);
            setPayables([]);
        } finally {
            setLoading(false);
        }
    }, [fetchWithAuth]);

    const fetchBusinessPartners = useCallback(async () => {
        const firstAvailable = async (paths) => {
            for (const path of paths) {
                try {
                    return await fetchWithAuth(path);
                } catch (error) {
                    console.warn(`${path} lookup failed`, error);
                }
            }
            return [];
        };
        const [customers, providers, agents] = await Promise.all([
            firstAvailable(["/customer", "/customers"]),
            firstAvailable(["/serviceProvider", "/service-provider"]),
            firstAvailable(["/globalAgent", "/global-agent"]),
        ]);
        setBusinessPartners([...customers, ...providers, ...agents]);
    }, [fetchWithAuth]);

    useEffect(() => {
        fetchPayables();
        fetchBusinessPartners();
    }, [fetchPayables, fetchBusinessPartners]);

    useEffect(() => {
        let active = true;

        const loadRates = async () => {
            try {
                const response = await fetch(
                    "https://api.exchangerate-api.com/v4/latest/USD",
                );

                if (!response.ok) throw new Error("Exchange rates fetch failed");

                const data = await response.json();

                if (active) setExchangeRates(data?.rates || null);
            } catch (error) {
                console.error("Exchange rates fetch error:", error);

                if (active) setExchangeRates(null);
            }
        };

        loadRates();

        return () => {
            active = false;
        };
    }, []);

    // Generic converter: `amount` expressed in `fromCurrency`, returned in
    // `toCurrency`. Returns the amount untouched when the two match, and null
    // when a rate is missing -- callers must treat null as "can't convert"
    // rather than silently computing a wrong number.
    const convertCurrency = useCallback(
        (amount, fromCurrency, toCurrency) => {
            const value = toNumber(amount);
            const from = normalizeCurrencyCode(fromCurrency);
            const to = normalizeCurrencyCode(toCurrency);

            if (!from || !to || from === to) return value;
            if (!exchangeRates?.[from] || !exchangeRates?.[to]) return null;

            return (value / exchangeRates[from]) * exchangeRates[to];
        },
        [exchangeRates],
    );

    // Same thing for a payable record, using the record's own cost currency as
    // the source and the user-selected display currency as the target.
    const convertCostAmount = useCallback(
        (item, amount, targetCurrency = selectedCurrency) =>
            convertCurrency(amount, getCurrency(item), targetCurrency),
        [convertCurrency, selectedCurrency],
    );

    // Component-scoped bindings of the payable balance helpers, pre-bound to
    // the live converter so a return invoice issued in another currency is
    // normalized into the payable's currency before it is subtracted. Every
    // call inside this component goes through these rather than the bare
    // module-level helpers, so a row, a filter and a KPI can never disagree
    // about the same record's balance.
    const returnSummaryOf = useCallback(
        (item) => getReturnSummary(item, convertCurrency),
        [convertCurrency],
    );

    const netPayableOf = useCallback(
        (item) => getNetPayable(item, convertCurrency),
        [convertCurrency],
    );

    const openAmountOf = useCallback(
        (item) => getOpenAmount(item, convertCurrency),
        [convertCurrency],
    );

    const supplierCreditOf = useCallback(
        (item) => getSupplierCreditAmount(item, convertCurrency),
        [convertCurrency],
    );

    const netMasterCostOf = useCallback(
        (item) => getNetMasterCost(item, convertCurrency),
        [convertCurrency],
    );

    const additionalPayableCostOf = useCallback(
        (item) => getAdditionalPayableCost(item, convertCurrency),
        [convertCurrency],
    );

    const paymentStatusOf = useCallback(
        (item) => getEffectivePaymentStatus(item, convertCurrency),
        [convertCurrency],
    );

    const partyCountryLookup = useMemo(() => {
        const lookup = new Map();
        businessPartners.forEach((record) => {
            const item = record?.attributes || record;
            const countries = [
                item?.customerCountry,
                item?.serviceProviderCountry,
                item?.globalAgentCountry,
                ...(Array.isArray(item?.servicesCountry) ? item.servicesCountry : []),
            ].filter(Boolean);
            [
                record?.id,
                item?._id,
                item?.id,
                item?.name,
                item?.nameKey,
                item?.companyName,
                item?.customerNo,
                item?.serviceProviderNo,
                item?.globalAgentNo,
            ]
                .map(normalizeLookupKey)
                .filter(Boolean)
                .forEach((key) =>
                    lookup.set(key, [...(lookup.get(key) || []), ...countries]),
                );
        });
        return lookup;
    }, [businessPartners]);

    const statusOptions = useMemo(() => {
        const unique = (getter) => [
            "all",
            ...Array.from(
                new Set(
                    payables.map((item) => String(getter(item)).trim()).filter(Boolean),
                ),
            ),
        ];

        return {
            payment: unique(paymentStatusOf),
            approval: unique(getApprovalStatus),
        };
    }, [payables, paymentStatusOf]);

    const returnStatusOptions = useMemo(
        () => [
            "all",
            ...Array.from(
                new Set(payables.map((item) => returnSummaryOf(item).status)),
            ).sort(),
        ],
        [payables, returnSummaryOf],
    );

    const partyOptions = useMemo(() => {
        const unique = new Map();

        payables.forEach((item) => {
            const customerName = getCustomerName(item);
            const supplierName = getSupplierName(item);

            if (customerName && customerName !== "N/A") {
                unique.set(`CUSTOMER-${customerName}`, {
                    type: "CUSTOMER",
                    name: customerName,
                    label: `Customer - ${customerName}`,
                });
            }

            if (supplierName && supplierName !== "N/A") {
                unique.set(`SUPPLIER-${supplierName}`, {
                    type: "SUPPLIER",
                    name: supplierName,
                    label: `Supplier - ${supplierName}`,
                });
            }
        });

        return Array.from(unique.values()).sort((a, b) =>
            a.label.localeCompare(b.label),
        );
    }, [payables]);

    const transportModeOptions = useMemo(
        () => Array.from(new Set(payables.map(getTransportMode))).sort(),
        [payables],
    );

    const currencyOptions = useMemo(
        () =>
            Array.from(
                new Set(
                    payables.map((item) => String(getCurrency(item)).toUpperCase()),
                ),
            ).sort(),
        [payables],
    );

    const filteredPayables = useMemo(() => {
        const searchText = search.toLowerCase().trim();
        const rangeStart = startDate ? new Date(`${startDate}T00:00:00`) : null;
        const rangeEnd = endDate ? new Date(`${endDate}T23:59:59`) : null;

        return payables.filter((item) => {
            const jobNo = String(getShipmentNo(item)).toLowerCase();
            const supplier = String(getSupplierName(item)).toLowerCase();
            const customer = String(getCustomerName(item)).toLowerCase();
            const payableDate = getDateValue(item);
            const invoiceNo = String(getInvoiceNo(item)).toLowerCase();
            const currentReturnStatus = returnSummaryOf(item).status.toLowerCase();
            const returnInvoiceNumbers = getLinkedReturnInvoices(item)
                .map(getReturnInvoiceNo)
                .join(" ")
                .toLowerCase();
            const currentPayment = String(
                paymentStatusOf(item),
            ).toLowerCase();
            const currentApproval = String(getApprovalStatus(item)).toLowerCase();
            const currentDueBucket = getDueBucket(
                getValue(item, "dueDate", "due_date"),
                paymentStatusOf(item),
            );

            const matchesSearch =
                !searchText ||
                jobNo.includes(searchText) ||
                supplier.includes(searchText) ||
                customer.includes(searchText) ||
                invoiceNo.includes(searchText) ||
                returnInvoiceNumbers.includes(searchText);

            const matchesStartDate =
                !rangeStart || (payableDate && payableDate >= rangeStart);
            const matchesEndDate =
                !rangeEnd || (payableDate && payableDate <= rangeEnd);
            const matchesParty =
                !selectedParty ||
                (selectedParty.type === "CUSTOMER"
                    ? getCustomerName(item) === selectedParty.name
                    : getSupplierName(item) === selectedParty.name);
            const matchesTransportMode =
                transportMode === "all" || getTransportMode(item) === transportMode;
            const matchesInvoiceOrigin =
                invoiceOrigin === "all" || getInvoiceOrigin(item) === invoiceOrigin;
            const matchesCurrency =
                currencyFilter === "all" ||
                String(getCurrency(item)).toUpperCase() === currencyFilter;
            const matchesGeographicScope =
                geographicScope === "all" ||
                getGeographicScope(item, partyCountryLookup) === geographicScope;

            const matchesPayment =
                paymentStatus === "all" ||
                currentPayment === paymentStatus.toLowerCase();

            const matchesApproval =
                approvalStatus === "all" ||
                currentApproval === approvalStatus.toLowerCase();

            const matchesDueBucket =
                dueBucket === "all" ||
                currentDueBucket.toLowerCase() === dueBucket.toLowerCase();

            const matchesReturnStatus =
                returnStatus === "all" ||
                currentReturnStatus === returnStatus.toLowerCase();

            return (
                matchesSearch &&
                matchesStartDate &&
                matchesEndDate &&
                matchesParty &&
                matchesTransportMode &&
                matchesInvoiceOrigin &&
                matchesCurrency &&
                matchesGeographicScope &&
                matchesPayment &&
                matchesApproval &&
                matchesDueBucket &&
                matchesReturnStatus
            );
        });
    }, [
        payables,
        search,
        paymentStatus,
        approvalStatus,
        dueBucket,
        startDate,
        endDate,
        selectedParty,
        transportMode,
        invoiceOrigin,
        currencyFilter,
        geographicScope,
        returnStatus,
        partyCountryLookup,
        returnSummaryOf,
        paymentStatusOf,
    ]);

    const stats = useMemo(() => {
        const uniqueJobCosts = Array.from(
            filteredPayables
                .reduce((jobs, item) => {
                    const key = getFinanceJobKey(item);
                    const current = jobs.get(key);

                    if (!current || netMasterCostOf(item) > netMasterCostOf(current)) {
                        jobs.set(key, item);
                    }

                    return jobs;
                }, new Map())
                .values(),
        );

        const grossPayable = filteredPayables.reduce(
            (sum, item) =>
                sum + (convertCostAmount(item, getGrossPayable(item)) ?? 0),
            0,
        );

        const returnedAmount = filteredPayables.reduce(
            (sum, item) =>
                sum +
                (convertCostAmount(item, returnSummaryOf(item).issuedAmount) ?? 0),
            0,
        );

        const totalPayable = filteredPayables.reduce(
            (sum, item) => sum + (convertCostAmount(item, netPayableOf(item)) ?? 0),
            0,
        );

        const paidAmount = filteredPayables.reduce(
            (sum, item) => sum + (convertCostAmount(item, getPaidAmount(item)) ?? 0),
            0,
        );

        const openPayable = filteredPayables.reduce(
            (sum, item) => sum + (convertCostAmount(item, openAmountOf(item)) ?? 0),
            0,
        );

        const supplierCredit = filteredPayables.reduce(
            (sum, item) =>
                sum + (convertCostAmount(item, supplierCreditOf(item)) ?? 0),
            0,
        );

        const overdueAmount = filteredPayables
            .filter(
                (item) =>
                    getDueBucket(
                        getValue(item, "dueDate", "due_date"),
                        paymentStatusOf(item),
                    ) === "Overdue",
            )
            .reduce(
                (sum, item) =>
                    sum + (convertCostAmount(item, openAmountOf(item)) ?? 0),
                0,
            );

        const buckets = filteredPayables.reduce(
            (accumulator, item) => {
                const bucket = getDueBucket(
                    getValue(item, "dueDate", "due_date"),
                    paymentStatusOf(item),
                );

                if (accumulator[bucket] !== undefined) {
                    accumulator[bucket] +=
                        convertCostAmount(item, openAmountOf(item)) ?? 0;
                }
                return accumulator;
            },
            {
                Overdue: 0,
                "Due Today": 0,
                "This Week": 0,
                "This Month": 0,
                Later: 0,
                "No Due Date": 0,
            },
        );

        const criticalCount = filteredPayables.filter(
            (item) =>
                getValue(item, "criticalSupplier", "critical_supplier", false) ===
                true ||
                String(getValue(item, "paymentPriority", "payment_priority", ""))
                    .toLowerCase()
                    .includes("high"),
        ).length;

        const masterCost = uniqueJobCosts.reduce(
            (sum, item) =>
                sum + (convertCostAmount(item, netMasterCostOf(item)) ?? 0),
            0,
        );

        const buyingRate = uniqueJobCosts.reduce(
            (sum, item) => sum + (convertCostAmount(item, getBuyingRate(item)) ?? 0),
            0,
        );

        const additionalCost = filteredPayables.reduce(
            (sum, item) =>
                sum + (convertCostAmount(item, additionalPayableCostOf(item)) ?? 0),
            0,
        );

        const includedInMasterCount = filteredPayables.filter(
            isIncludedInMasterCost,
        ).length;

        const paymentRate = totalPayable
            ? (Math.min(paidAmount, totalPayable) / totalPayable) * 100
            : 0;

        return {
            count: filteredPayables.length,
            grossPayable,
            returnedAmount,
            totalPayable,
            paidAmount,
            openPayable,
            supplierCredit,
            overdueAmount,
            criticalCount,
            buckets,
            masterCost,
            buyingRate,
            additionalCost,
            includedInMasterCount,
            paymentRate,
        };
    }, [
        filteredPayables,
        convertCostAmount,
        returnSummaryOf,
        netPayableOf,
        openAmountOf,
        supplierCreditOf,
        netMasterCostOf,
        additionalPayableCostOf,
        paymentStatusOf,
    ]);

    const transportSummary = useMemo(() => {
        const initial = {
            Ocean: {
                count: 0,
                total: 0,
                paid: 0,
                open: 0,
                overdue: 0,
                paymentRate: 0,
            },
            Road: {
                count: 0,
                total: 0,
                paid: 0,
                open: 0,
                overdue: 0,
                paymentRate: 0,
            },
            Air: { count: 0, total: 0, paid: 0, open: 0, overdue: 0, paymentRate: 0 },
            Manual: {
                count: 0,
                total: 0,
                paid: 0,
                open: 0,
                overdue: 0,
                paymentRate: 0,
            },
        };
        filteredPayables.forEach((item) => {
            const mode = getTransportMode(item);
            const bucket = initial[mode] || initial.Manual;
            const total = convertCostAmount(item, netPayableOf(item)) ?? 0;
            const paid = convertCostAmount(item, getPaidAmount(item)) ?? 0;
            const open = convertCostAmount(item, openAmountOf(item)) ?? 0;

            bucket.count += 1;
            bucket.total += total;
            bucket.paid += paid;
            bucket.open += open;
            if (
                getDueBucket(
                    getValue(item, "dueDate", "due_date"),
                    paymentStatusOf(item),
                ) === "Overdue"
            )
                bucket.overdue += open;
        });
        Object.values(initial).forEach((item) => {
            item.paymentRate = item.total
                ? (Math.min(item.paid, item.total) / item.total) * 100
                : 0;
        });
        return initial;
    }, [
        filteredPayables,
        convertCostAmount,
        netPayableOf,
        openAmountOf,
        paymentStatusOf,
    ]);

    const shipmentTypeSummary = useMemo(() => {
        const result = {};
        filteredPayables.forEach((item) => {
            const type = getShipmentType(item);
            if (!result[type]) result[type] = { count: 0, open: 0 };
            result[type].count += 1;
            result[type].open += convertCostAmount(item, openAmountOf(item)) ?? 0;
        });
        return Object.entries(result).sort((a, b) => b[1].open - a[1].open);
    }, [filteredPayables, convertCostAmount, openAmountOf]);

    const resolvePayableRecord = async (item) => {
        const recordIds = [
            item?.financeJobId,
            item?.finance_job_id,
            item?.jobFinanceId,
            item?.job_finance_id,
            item?._id,
            item?.id,
        ].filter(Boolean);
        const uniqueIds = [...new Set(recordIds.map(String))];

        for (const recordId of uniqueIds) {
            const detailPaths = [
                `/finance/manual-invoices/${recordId}`,
                `/finance/supplier-invoices/${recordId}`,
                `/invoice/${recordId}`,
                `/finance/jobs/${recordId}`,
            ];

            for (const path of detailPaths) {
                try {
                    const response = await fetch(`${API_URL}${path}`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });

                    if (!response.ok) continue;

                    const payload = await response.json();
                    const detail =
                        payload?.data?.invoice ||
                        payload?.data?.job ||
                        payload?.data?.data ||
                        payload?.invoice ||
                        payload?.job ||
                        payload?.data ||
                        payload;

                    if (detail && typeof detail === "object" && !Array.isArray(detail)) {
                        return {
                            ...item,
                            ...detail,
                            invoiceItems:
                                detail?.invoiceItems ||
                                detail?.invoice_items ||
                                item?.invoiceItems ||
                                item?.invoice_items ||
                                [],
                        };
                    }
                } catch (error) {
                    console.warn(`${path} payable detail lookup failed`, error);
                }
            }
        }

        return item;
    };

    const openFinanceDetail = async (item) => {
        const resolvedItem = await resolvePayableRecord(item);
        const invoiceId = resolvedItem?._id || resolvedItem?.id;

        if (getInvoiceOrigin(resolvedItem) === "manual") {
            if (!invoiceId) {
                appAlert(financeText("Manual invoice ID not found."));
                return;
            }

            navigate(`/invoice/finance/jobs/${invoiceId}`, {
                state: {
                    job: resolvedItem,
                    financeJobId: invoiceId,
                    invoiceOrigin: "manual",
                },
            });
            return;
        }

        const financeJobId =
            resolvedItem?.financeJobId ||
            resolvedItem?.finance_job_id ||
            resolvedItem?.jobFinanceId ||
            resolvedItem?.job_finance_id;

        if (!financeJobId) {
            appAlert(
                financeText(
                    "Finance Job ID not found. Payable record must be linked with financeJobId."
                ),
            );
            return;
        }

        navigate(`/invoice/finance/jobs/${financeJobId}`, {
            state: {
                job: resolvedItem,
                financeJobId,
            },
        });
    };

    const openLatestReturnInvoice = (item) => {
        const latestReturnInvoice = returnSummaryOf(item).latestReturnInvoice;
        const returnInvoiceId = getEntityId(
            latestReturnInvoice?._id || latestReturnInvoice?.id,
        );

        if (!returnInvoiceId) {
            appAlert(financeText("Return invoice ID not found."));
            return;
        }

        navigate(`/invoice/finance/return-invoices/${returnInvoiceId}`, {
            state: {
                returnInvoice: latestReturnInvoice,
                originalInvoice: item,
                originalInvoiceId:
                    getOriginalInvoiceId(latestReturnInvoice) ||
                    getEntityId(item?._id || item?.id),
            },
        });
    };

    const markPaid = async (item) => {
        const id = item?._id || item?.id;

        if (!id) {
            appAlert(financeText("Payable ID not found!"));
            return;
        }

        try {
            const updatePath =
                getInvoiceOrigin(item) === "manual"
                    ? `/finance/manual-invoices/${id}`
                    : `/finance/supplier-invoices/${id}`;
            const response = await fetch(`${API_URL}${updatePath}`, {
                method: "PATCH",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    paymentStatus: "Paid",
                    paidAmount: netPayableOf(item),
                    paidAt: new Date().toISOString(),
                }),
            });

            if (!response.ok) {
                throw new Error("Payable payment update failed");
            }

            const result = await response.json();
            const updated =
                result?.data?.invoice ||
                result?.data?.payable ||
                result?.invoice ||
                result?.payable ||
                result?.data ||
                result;

            setPayables((previous) =>
                previous.map((current) =>
                    String(current?._id || current?.id) === String(id)
                        ? {
                            ...current,
                            ...updated,
                            returnInvoices: getLinkedReturnInvoices(current),
                        }
                        : current,
                ),
            );
        } catch (error) {
            console.error("Mark paid error:", error);
            appAlert(financeText("Payment status could not be updated."));
        }
    };

    const resetFilters = () => {
        setSearch("");
        setPaymentStatus("all");
        setApprovalStatus("all");
        setDueBucket("all");
        setStartDate(`${currentYear}-01-01`);
        setEndDate(todayValue);
        setSelectedParty(null);
        setTransportMode("all");
        setInvoiceOrigin("all");
        setCurrencyFilter("all");
        setGeographicScope("all");
        setReturnStatus("all");
    };

    const exportCsv = () => {
        const headers = [
            "Shipment",
            "Invoice No",
            "Supplier",
            "Customer",
            "Geographic Scope",
            "Transport Mode",
            "Invoice Origin",
            "Cost Currency",
            "Reporting Currency",
            "Gross Payable",
            "Issued Returns",
            "Net Payable",
            "Paid",
            "Open",
            "Supplier Credit",
            "Operational Cost",
            "Buying Rate",
            "Master Cost",
            "Additional Cost",
            "Included In Master Cost",
            "Due Date",
            "Due Bucket",
            "Approval",
            "Payment",
            "Priority",
            "Return Status",
            "Return Documents",
            "Latest Return Invoice",
        ];

        const rows = filteredPayables.map((item) => {
            const returnSummary = returnSummaryOf(item);

            return [
                getShipmentNo(item),
                getInvoiceNo(item),
                getSupplierName(item),
                getCustomerName(item),
                getGeographicLabel(item, partyCountryLookup),
                getTransportMode(item),
                getInvoiceOrigin(item).toUpperCase(),
                getCurrency(item),
                selectedCurrency,
                convertCostAmount(item, getGrossPayable(item)) ?? "Rate unavailable",
                convertCostAmount(item, returnSummary.issuedAmount) ??
                "Rate unavailable",
                convertCostAmount(item, netPayableOf(item)) ?? "Rate unavailable",
                convertCostAmount(item, getPaidAmount(item)) ?? "Rate unavailable",
                convertCostAmount(item, openAmountOf(item)) ?? "Rate unavailable",
                convertCostAmount(item, supplierCreditOf(item)) ??
                "Rate unavailable",
                convertCostAmount(item, getOperationalCost(item)) ?? "Rate unavailable",
                convertCostAmount(item, getBuyingRate(item)) ?? "Rate unavailable",
                convertCostAmount(item, netMasterCostOf(item)) ?? "Rate unavailable",
                convertCostAmount(item, additionalPayableCostOf(item)) ??
                "Rate unavailable",
                isIncludedInMasterCost(item) ? "Yes" : "No",
                formatDate(getValue(item, "dueDate", "due_date")),
                getDueBucket(
                    getValue(item, "dueDate", "due_date"),
                    paymentStatusOf(item),
                ),
                getApprovalStatus(item),
                paymentStatusOf(item),
                getValue(item, "paymentPriority", "payment_priority", "Normal"),
                returnSummary.status,
                returnSummary.count,
                getReturnInvoiceNo(returnSummary.latestReturnInvoice || {}),
            ];
        });

        const csv = [headers, ...rows]
            .map((row) =>
                row
                    .map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`)
                    .join(","),
            )
            .join("\n");

        const blob = new Blob([`\uFEFF${csv}`], {
            type: "text/csv;charset=utf-8;",
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        const modeName = transportMode === "all" ? "all-modes" : transportMode;
        const scopeName =
            geographicScope === "all" ? "all-scopes" : geographicScope.toLowerCase();
        link.setAttribute(
            "download",
            `payables-management-${modeName}-${scopeName}-${todayValue}.csv`,
        );

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    };

    const tableData = useMemo(
        () => ({
            columns: [
                {
                    Header: financeText("SHIPMENT"),
                    accessor: "jobNo",
                    width: "12%",
                    Cell: ({ row }) => {
                        const item = row.original;

                        return (
                            <MDBox display="flex" alignItems="center" gap={1.1} py={0.35}>
                                <MDBox
                                    width={38}
                                    height={38}
                                    borderRadius="11px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    flexShrink={0}
                                    sx={{
                                        background:
                                            "#EEF4FF",
                                        color: COLORS.blue,
                                        border: "1px solid #D1E0FF",
                                    }}
                                >
                                    <ReceiptLong sx={{ fontSize: "18px !important" }} />
                                </MDBox>

                                <MDBox minWidth={0}>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        noWrap
                                        sx={{
                                            cursor: "pointer",
                                            color: COLORS.navy,
                                            display: "block",
                                            maxWidth: 140,
                                            "&:hover": {
                                                color: COLORS.blue,
                                            },
                                        }}
                                        onClick={() => openFinanceDetail(item)}
                                    >
                                        {getShipmentNo(item)}
                                    </MDTypography>

                                    <MDBox
                                        display="flex"
                                        alignItems="center"
                                        gap={0.6}
                                        mt={0.35}
                                        flexWrap="wrap"
                                    >
                                        <Chip
                                            size="small"
                                            label={financeText(getTransportMode(item))}
                                            sx={{
                                                height: 20,
                                                color: (
                                                    MODE_CONFIG[getTransportMode(item)] ||
                                                    MODE_CONFIG.Manual
                                                ).color,
                                                background: (
                                                    MODE_CONFIG[getTransportMode(item)] ||
                                                    MODE_CONFIG.Manual
                                                ).bg,
                                                fontSize: "9px",
                                                fontWeight: 800,
                                            }}
                                        />
                                        <Chip
                                            size="small"
                                            label={getShipmentType(item)}
                                            sx={{
                                                height: 20,
                                                background: "#F2F4F7",
                                                color: COLORS.subtext,
                                                fontSize: "9px",
                                                fontWeight: 700,
                                            }}
                                        />
                                    </MDBox>
                                </MDBox>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("SUPPLIER / INVOICE"),
                    accessor: "supplierName",
                    width: "17%",
                    Cell: ({ row }) => {
                        const item = row.original;
                        const name = getSupplierName(item);

                        return (
                            <MDBox display="flex" alignItems="center" gap={1}>
                                <Avatar
                                    sx={{
                                        width: 34,
                                        height: 34,
                                        background: "#0B1F3A",
                                        color: "#FFFFFF",
                                        fontSize: "11px",
                                        fontWeight: 800,
                                    }}
                                >
                                    {name.slice(0, 2).toUpperCase()}
                                </Avatar>

                                <MDBox minWidth={0}>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        noWrap
                                        sx={{
                                            color: COLORS.text,
                                            maxWidth: 155,
                                            display: "block",
                                        }}
                                    >
                                        {name}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        noWrap
                                        display="block"
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {getInvoiceNo(item)}
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("AMOUNTS"),
                    accessor: "netPayableAmount",
                    align: "right",
                    width: "13%",
                    Cell: ({ row }) => {
                        const item = row.original;
                        const currency = getCurrency(item);
                        const returnSummary = returnSummaryOf(item);

                        return (
                            <MDBox textAlign="right">
                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{ color: COLORS.subtext }}
                                >
                                    {financeText("Gross")} {formatCompact(getGrossPayable(item), currency)}
                                </MDTypography>

                                {returnSummary.issuedAmount > 0 && (
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        fontWeight="bold"
                                        sx={{ color: COLORS.purple }}
                                    >
                                        Return -
                                        {formatCompact(returnSummary.issuedAmount, currency)}
                                    </MDTypography>
                                )}

                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{ color: COLORS.red }}
                                >
                                    {financeText("Net")} {formatCompact(netPayableOf(item), currency)}
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{ color: COLORS.green }}
                                >
                                    {financeText("Paid")} {formatCompact(getPaidAmount(item), currency)}
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    fontWeight="bold"
                                    sx={{ color: COLORS.amber }}
                                >
                                    {financeText("Open")} {formatCompact(openAmountOf(item), currency)}
                                </MDTypography>

                                {supplierCreditOf(item) > 0 && (
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        fontWeight="bold"
                                        sx={{ color: COLORS.purple }}
                                    >
                                        Credit{" "}
                                        {formatCompact(supplierCreditOf(item), currency)}
                                    </MDTypography>
                                )}
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("COST RELATION"),
                    accessor: "includedInMasterCost",
                    width: "12%",
                    Cell: ({ row }) => {
                        const item = row.original;
                        const included = isIncludedInMasterCost(item);

                        return (
                            <MDBox>
                                <Chip
                                    size="small"
                                    label={financeText(included ? "Included" : "Additional")}
                                    sx={{
                                        color: included ? COLORS.blue : COLORS.purple,
                                        background: included
                                            ? COLORS.blueLight
                                            : COLORS.purpleLight,
                                        fontWeight: 700,
                                        borderRadius: "8px",
                                    }}
                                />

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    mt={0.5}
                                    sx={{ color: COLORS.subtext }}
                                >
                                    {included
                                        ? financeText("Already in master cost")
                                        : formatCompact(
                                            additionalPayableCostOf(item),
                                            getCurrency(item),
                                        )}
                                </MDTypography>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("RETURN STATUS"),
                    accessor: "returnStatus",
                    width: "12%",
                    Cell: ({ row }) => {
                        const summary = returnSummaryOf(row.original);
                        const style =
                            RETURN_STATUS_STYLES[summary.status] ||
                            RETURN_STATUS_STYLES["No Return"];

                        return (
                            <MDBox>
                                <Chip
                                    size="small"
                                    label={financeText(summary.status)}
                                    onClick={
                                        summary.latestReturnInvoice
                                            ? () => openLatestReturnInvoice(row.original)
                                            : undefined
                                    }
                                    sx={{
                                        height: 24,
                                        color: style.color,
                                        background: style.background,
                                        border: `1px solid ${style.border}`,
                                        fontWeight: 800,
                                        borderRadius: "8px",
                                        cursor: summary.latestReturnInvoice ? "pointer" : "default",
                                    }}
                                />
                                {summary.count > 0 && (
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        mt={0.45}
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {summary.count}{" "}
                                        {financeText(
                                            summary.count === 1
                                                ? "return document"
                                                : "return documents"
                                        )}
                                    </MDTypography>
                                )}
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("DUE / BUCKET"),
                    accessor: "dueDate",
                    width: "14%",
                    Cell: ({ row }) => {
                        const item = row.original;
                        const date = getValue(item, "dueDate", "due_date");
                        const days = getDueDays(date, paymentStatusOf(item));
                        const bucket = getDueBucket(date, paymentStatusOf(item));

                        const color =
                            bucket === "Overdue"
                                ? COLORS.red
                                : bucket === "Due Today" || bucket === "This Week"
                                    ? COLORS.amber
                                    : COLORS.green;

                        const background =
                            bucket === "Overdue"
                                ? COLORS.redLight
                                : bucket === "Due Today" || bucket === "This Week"
                                    ? COLORS.amberLight
                                    : COLORS.greenLight;

                        return (
                            <MDBox>
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{
                                        color: days !== null && days < 0 ? COLORS.red : COLORS.text,
                                    }}
                                >
                                    {formatDate(date)}
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{
                                        color:
                                            days !== null && days < 0 ? COLORS.red : COLORS.subtext,
                                    }}
                                >
                                    {bucket === "Closed"
                                        ? financeText("Closed")
                                        : days === null
                                        ? financeText("No due date")
                                        : days < 0
                                            ? `${Math.abs(days)} ${financeText("days overdue")}`
                                            : days === 0
                                                ? financeText("Due today")
                                                : `${days} ${financeText("days left")}`}
                                </MDTypography>

                                <Chip
                                    size="small"
                                    label={financeText(bucket)}
                                    sx={{
                                        mt: 0.5,
                                        height: 22,
                                        color,
                                        background,
                                        fontWeight: 700,
                                        borderRadius: "7px",
                                    }}
                                />
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("APPROVAL"),
                    accessor: "approvalStatus",
                    width: "9%",
                    Cell: ({ row }) => (
                        <FinanceStatusChip status={getApprovalStatus(row.original)} />
                    ),
                },
                {
                    Header: financeText("PAYMENT"),
                    accessor: "paymentStatus",
                    width: "9%",
                    Cell: ({ row }) => (
                        <FinanceStatusChip
                            status={paymentStatusOf(row.original)}
                        />
                    ),
                },
                {
                    Header: financeText("PRIORITY"),
                    accessor: "paymentPriority",
                    width: "9%",
                    Cell: ({ row }) => {
                        const priority = getValue(
                            row.original,
                            "paymentPriority",
                            "payment_priority",
                            "Normal",
                        );

                        const high = String(priority).toLowerCase().includes("high");

                        return (
                            <Chip
                                size="small"
                                label={financeText(priority)}
                                sx={{
                                    color: high ? COLORS.red : COLORS.blue,
                                    background: high ? COLORS.redLight : COLORS.blueLight,
                                    fontWeight: 700,
                                    borderRadius: "8px",
                                }}
                            />
                        );
                    },
                },
                {
                    Header: financeText("ACTIONS"),
                    accessor: "actions",
                    align: "right",
                    width: "10%",
                    Cell: ({ row }) => {
                        const item = row.original;
                        const isPaid = openAmountOf(item) <= 0.01;
                        const returnSummary = returnSummaryOf(item);
                        const paymentTooltip =
                            supplierCreditOf(item) > 0.01
                                ? financeText("Supplier credit must be reconciled")
                                : returnSummary.status === "Full Return"
                                    ? financeText("Payable fully returned")
                                    : isPaid
                                        ? financeText("Already paid")
                                        : financeText("Mark as paid");

                        return (
                            <MDBox display="flex" justifyContent="flex-end" gap={0.55}>
                                <Tooltip title={financeText("Finance detail")} arrow>
                                    <IconButton
                                        size="small"
                                        onClick={() => openFinanceDetail(item)}
                                        sx={{
                                            color: COLORS.blue,
                                            background: COLORS.blueLight,
                                            border: "1px solid #B2DDFF",
                                            "&:hover": {
                                                background: "#D1E9FF",
                                            },
                                        }}
                                    >
                                        <Visibility sx={{ fontSize: "17px !important" }} />
                                    </IconButton>
                                </Tooltip>

                                {returnSummary.latestReturnInvoice && (
                                    <Tooltip title={financeText("View latest return invoice")} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => openLatestReturnInvoice(item)}
                                            sx={{
                                                color: COLORS.purple,
                                                background: COLORS.purpleLight,
                                                border: "1px solid #D9D6FE",
                                                "&:hover": {
                                                    background: "#E9E7FD",
                                                },
                                            }}
                                        >
                                            <RestartAlt sx={{ fontSize: "17px !important" }} />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                <Tooltip title={paymentTooltip} arrow>
                  <span>
                    <IconButton
                        size="small"
                        disabled={isPaid}
                        onClick={() => markPaid(item)}
                        sx={{
                            color: COLORS.green,
                            background: COLORS.greenLight,
                            border: "1px solid #ABEFC6",
                            "&:hover": {
                                background: "#D1FADF",
                            },
                            "&.Mui-disabled": {
                                background: "#F2F4F7",
                                borderColor: "#E4E7EC",
                            },
                        }}
                    >
                      <Paid sx={{ fontSize: "17px !important" }} />
                    </IconButton>
                  </span>
                                </Tooltip>
                            </MDBox>
                        );
                    },
                },
            ],
            rows: filteredPayables,
        }),
        [
            filteredPayables,
            financeLanguage,
            returnSummaryOf,
            netPayableOf,
            openAmountOf,
            supplierCreditOf,
            additionalPayableCostOf,
            paymentStatusOf,
        ],
    );

    const bucketMeta = {
        Overdue: {
            color: COLORS.red,
            background: COLORS.redLight,
        },
        "Due Today": {
            color: COLORS.amber,
            background: COLORS.amberLight,
        },
        "This Week": {
            color: COLORS.amber,
            background: COLORS.amberLight,
        },
        "This Month": {
            color: COLORS.blue,
            background: COLORS.blueLight,
        },
        Later: {
            color: COLORS.green,
            background: COLORS.greenLight,
        },
        "No Due Date": {
            color: COLORS.purple,
            background: COLORS.purpleLight,
        },
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox
                pt={6}
                pb={3}
                sx={{
                    minHeight: "100vh",
                    background: COLORS.page,
                    mx: { xs: -2, md: -3 },
                    px: { xs: 2, md: 3 },
                }}
            >
                <Card
                    sx={{
                        borderRadius: "22px",
                        color: COLORS.white,
                        background:
                            "linear-gradient(125deg, #081A31 0%, #0D2D52 52%, #175CD3 135%)",
                        boxShadow: "0 18px 46px rgba(11, 31, 58, 0.22)",
                        overflow: "hidden",
                        position: "relative",
                        mb: 3,
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            width: 360,
                            height: 360,
                            borderRadius: "50%",
                            right: -130,
                            top: -180,
                            background: "rgba(255,255,255,.07)",
                        },
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            width: 230,
                            height: 230,
                            borderRadius: "50%",
                            right: 120,
                            bottom: -170,
                            background: "rgba(11,165,236,.16)",
                        },
                    }}
                >
                    <MDBox p={{ xs: 2.5, md: 3.5 }} position="relative" zIndex={1}>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} lg={7}>
                                <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                                    <MDBox
                                        width={34}
                                        height={34}
                                        borderRadius="10px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                            background: "rgba(255,255,255,.12)",
                                        }}
                                    >
                                        <WorkspacePremium
                                            sx={{
                                                color: "#FFFFFF",
                                                fontSize: 20,
                                            }}
                                        />
                                    </MDBox>

                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            color: "rgba(255,255,255,.72)",
                                            letterSpacing: ".12em",
                                        }}
                                    >
                                        {financeText("CORPORATE PAYABLES CONTROL")}
                                    </MDTypography>
                                </MDBox>

                                <MDTypography
                                    variant="h2"
                                    fontWeight="bold"
                                    sx={{
                                        color: "#FFFFFF",
                                        lineHeight: 1.12,
                                    }}
                                >
                                    {financeText("Payables Management")}
                                </MDTypography>

                                <MDTypography
                                    variant="body2"
                                    mt={1}
                                    sx={{
                                        color: "rgba(255,255,255,.72)",
                                        maxWidth: 720,
                                    }}
                                >
                                    {financeText(
                                        "Manage supplier debt, maturity planning, payment priorities, master-cost relationships and overdue exposure from one banking-grade workspace."
                                    )}
                                </MDTypography>

                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    flexWrap="wrap"
                                    gap={1}
                                    mt={2.5}
                                >
                                    <Chip
                                        icon={
                                            <CheckCircle
                                                sx={{
                                                    color: "#75E0A7 !important",
                                                }}
                                            />
                                        }
                                        label={`${filteredPayables.length} ${financeText("payable records")}`}
                                        sx={{
                                            color: "#FFFFFF",
                                            background: "rgba(255,255,255,.10)",
                                            border: "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />

                                    <Chip
                                        icon={
                                            <CalendarMonth
                                                sx={{
                                                    color: "#B2DDFF !important",
                                                }}
                                            />
                                        }
                                        label={`${financeText("Updated")} ${lastUpdated.toLocaleTimeString([], {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}`}
                                        sx={{
                                            color: "#FFFFFF",
                                            background: "rgba(255,255,255,.10)",
                                            border: "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />
                                </MDBox>
                            </Grid>

                            <Grid item xs={12} lg={5}>
                                <MDBox
                                    p={2}
                                    borderRadius="16px"
                                    sx={{
                                        background: "rgba(255,255,255,.09)",
                                        border: "1px solid rgba(255,255,255,.13)",
                                        backdropFilter: "blur(8px)",
                                    }}
                                >
                                    <MDBox
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        mb={1}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            fontWeight="bold"
                                            sx={{
                                                color: "rgba(255,255,255,.72)",
                                            }}
                                        >
                                            {financeText("OPEN PAYABLES")}
                                        </MDTypography>

                                        <AccountBalanceWallet sx={{ color: "#FEDF89" }} />
                                    </MDBox>

                                    <MDTypography
                                        variant="h3"
                                        fontWeight="bold"
                                        sx={{ color: "#FFFFFF" }}
                                    >
                                        {formatCompact(stats.openPayable, selectedCurrency)}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        sx={{
                                            color: "rgba(255,255,255,.68)",
                                        }}
                                    >
                                        {financeText("Payment rate")} {stats.paymentRate.toFixed(1)}% · {financeText("Overdue")}{" "}
                                        {formatCompact(stats.overdueAmount, selectedCurrency)}
                                    </MDTypography>

                                    <MDBox display="flex" gap={1} mt={2} alignItems="stretch">
                                        <MDBox
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",
                                                p: "4px",
                                                background: "rgba(255,255,255,.10)",
                                                border: "1px solid rgba(255,255,255,.20)",
                                                borderRadius: "12px",
                                                boxShadow: "inset 0 1px 2px rgba(0,0,0,.10)",
                                                flexShrink: 0,
                                            }}
                                        >
                                            {[
                                                { value: "en", label: "EN", flag: "/flags/gb.svg", title: "English" },
                                                { value: "tr", label: "TR", flag: "/flags/tr.svg", title: "Türkçe" },
                                            ].map((language) => (
                                                <MDBox
                                                    key={language.value}
                                                    component="button"
                                                    type="button"
                                                    onClick={() =>
                                                        changeFinanceLanguage({
                                                            target: { value: language.value },
                                                        })
                                                    }
                                                    title={language.title}
                                                    sx={{
                                                        appearance: "none",
                                                        WebkitAppearance: "none",
                                                        border: "none",
                                                        outline: "none",
                                                        cursor: "pointer",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        gap: "7px",
                                                        height: 38,
                                                        minWidth: 76,
                                                        px: 1.15,
                                                        borderRadius: "8px",
                                                        background:
                                                            financeLanguage === language.value
                                                                ? "#FFFFFF"
                                                                : "transparent",
                                                        color:
                                                            financeLanguage === language.value
                                                                ? COLORS.navy
                                                                : "#FFFFFF",
                                                        boxShadow:
                                                            financeLanguage === language.value
                                                                ? "0 3px 10px rgba(0,0,0,.18)"
                                                                : "none",
                                                    }}
                                                >
                                                    <MDBox
                                                        component="img"
                                                        src={language.flag}
                                                        alt={language.title}
                                                        sx={{
                                                            width: 24,
                                                            height: 17,
                                                            objectFit: "cover",
                                                            borderRadius: "3px",
                                                            display: "block",
                                                        }}
                                                    />
                                                    <MDTypography
                                                        component="span"
                                                        sx={{
                                                            fontSize: "12px",
                                                            fontWeight: 800,
                                                            color: "inherit",
                                                            lineHeight: 1,
                                                        }}
                                                    >
                                                        {language.label}
                                                    </MDTypography>
                                                </MDBox>
                                            ))}
                                        </MDBox>

                                        <MDButton
                                            variant="contained"
                                            onClick={() =>
                                                navigate("/invoice/finance/supplier-invoices")
                                            }
                                            sx={{
                                                background: "#FFFFFF !important",
                                                color: `${COLORS.navy} !important`,
                                                flex: 1,
                                                boxShadow: "none",
                                            }}
                                        >
                                            <ReceiptLong sx={{ mr: 0.5 }} />
                                            {financeText("Supplier Invoices")}
                                        </MDButton>

                                        <MDButton
                                            variant="contained"
                                            onClick={() =>
                                                navigate("/invoice/finance/manual-invoices/new")
                                            }
                                            sx={{
                                                background: `${COLORS.blue} !important`,
                                                color: "#FFFFFF !important",
                                                flex: 1,
                                                boxShadow: "none",
                                            }}
                                        >
                                            {financeText("Create Manual Invoice")}
                                        </MDButton>

                                        <Tooltip title={financeText("Refresh payables")}>
                                            <IconButton
                                                onClick={fetchPayables}
                                                sx={{
                                                    color: "#FFFFFF",
                                                    border: "1px solid rgba(255,255,255,.18)",
                                                    borderRadius: "10px",
                                                }}
                                            >
                                                <Refresh />
                                            </IconButton>
                                        </Tooltip>
                                    </MDBox>
                                </MDBox>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                <Card sx={{ ...cardSx, mb: 2.5 }}>
                    <MDBox p={{ xs: 2, md: 2.5 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label={financeText("Start Date")}
                                    value={startDate}
                                    onChange={(event) => setStartDate(event.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label={financeText("End Date")}
                                    value={endDate}
                                    onChange={(event) => setEndDate(event.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Customer / Supplier")}
                                    value={
                                        selectedParty
                                            ? `${selectedParty.type}-${selectedParty.name}`
                                            : "all"
                                    }
                                    onChange={(event) => {
                                        if (event.target.value === "all") {
                                            setSelectedParty(null);
                                            return;
                                        }

                                        setSelectedParty(
                                            partyOptions.find(
                                                (option) =>
                                                    `${option.type}-${option.name}` ===
                                                    event.target.value,
                                            ) || null,
                                        );
                                    }}
                                >
                                    <MenuItem value="all">{financeText("All Customers / Suppliers")}</MenuItem>
                                    {partyOptions.map((option) => {
                                        const value = `${option.type}-${option.name}`;
                                        return (
                                            <MenuItem key={value} value={value}>
                                                {option.type === "CUSTOMER" ? `${financeText("Customer")} - ${option.name}` : `${financeText("Supplier")} - ${option.name}`}
                                            </MenuItem>
                                        );
                                    })}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Invoice Origin")}
                                    value={invoiceOrigin}
                                    onChange={(event) => setInvoiceOrigin(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Invoice Origins")}</MenuItem>
                                    <MenuItem value="tms">TMS</MenuItem>
                                    <MenuItem value="manual">{financeText("Manual")}</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Geographic Scope")}
                                    value={geographicScope}
                                    onChange={(event) => setGeographicScope(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Geographic Scopes")}</MenuItem>
                                    <MenuItem value="DOMESTIC">{financeText("Domestic")}</MenuItem>
                                    <MenuItem value="INTERNATIONAL">{financeText("International")}</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Transport Mode")}
                                    value={transportMode}
                                    onChange={(event) => setTransportMode(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Transport Modes")}</MenuItem>
                                    {transportModeOptions.map((mode) => (
                                        <MenuItem key={financeText(mode)} value={financeText(mode)}>
                                            {financeText(mode)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Currency")}
                                    value={currencyFilter}
                                    onChange={(event) => setCurrencyFilter(event.target.value)}
                                    helperText={financeText("Filters which records are listed")}
                                >
                                    <MenuItem value="all">{financeText("All Currencies")}</MenuItem>
                                    {currencyOptions.map((currency) => (
                                        <MenuItem key={currency} value={currency}>
                                            {currency}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            {/* Sits next to the record-level "Currency" filter
                                above but does something different: it does not
                                change which payables are listed, only the
                                currency the aggregated totals are converted
                                into. */}
                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Display Currency")}
                                    value={selectedCurrency}
                                    onChange={(event) => setSelectedCurrency(event.target.value)}
                                    helperText={financeText("Currency used for totals")}
                                >
                                    {CURRENCY_OPTIONS.map((currency) => (
                                        <MenuItem key={currency} value={currency}>
                                            {currency}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Return Status")}
                                    value={returnStatus}
                                    onChange={(event) => setReturnStatus(event.target.value)}
                                >
                                    {returnStatusOptions.map((status) => (
                                        <MenuItem key={status} value={status}>
                                            {status === "all" ? financeText("All Return Statuses") : financeText(status)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                {loading ? (
                    <Grid container spacing={SPACE.rowGap}>
                        <Grid item xs={12} lg={7}>
                            <Skeleton variant="rounded" height={260} sx={{ borderRadius: SPACE.radiusCard }} />
                        </Grid>
                        <Grid item xs={12} lg={5}>
                            <Skeleton variant="rounded" height={260} sx={{ borderRadius: SPACE.radiusCard }} />
                        </Grid>
                    </Grid>
                ) : (
                    <>
                        {/* Same hero + secondary-tiles pattern as the Finance
                            Dashboard, from the shared FinanceUiKit. */}

                        <Grid container spacing={SPACE.rowGap} mt={0.5}>
                            <Grid item xs={12} sm={6} lg={4}>
                                <FinanceKpiTile
                                    title={financeText("Payables")}
                                    value={formatNumber(stats.count)}
                                    subtitle={financeText("Filtered supplier liabilities")}
                                    icon={<ReceiptLong />}
                                    tone="navy"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={4}>
                                <FinanceKpiTile
                                    title={financeText("Payment Rate")}
                                    value={`${stats.paymentRate.toFixed(1)}%`}
                                    subtitle={financeText("Paid versus net payable")}
                                    icon={<Paid />}
                                    tone="green"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={4}>
                                <FinanceKpiTile
                                    title={financeText("Supplier Credit")}
                                    value={formatCompact(stats.supplierCredit, selectedCurrency)}
                                    subtitle={financeText("Overpayment applied as credit")}
                                    icon={<AccountBalanceWallet />}
                                    tone="blue"
                                />
                            </Grid>
                        </Grid>




                        <Card sx={{ ...cardSx, mt: 2.5 }}>
                            <MDBox p={3}>
                                <MDBox
                                    display="flex"
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    justifyContent="space-between"
                                    flexDirection={{ xs: "column", md: "row" }}
                                    gap={1.5}
                                    mb={2.5}
                                >
                                    <MDBox display="flex" alignItems="center" gap={1}>
                                        <MDBox
                                            width={38}
                                            height={38}
                                            borderRadius="11px"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            sx={{
                                                color: COLORS.blue,
                                                background: COLORS.blueLight,
                                                border: "1px solid #B2DDFF",
                                            }}
                                        >
                                            <FilterAlt sx={{ fontSize: 19 }} />
                                        </MDBox>

                                        <MDBox>
                                            <MDTypography
                                                variant="h6"
                                                fontWeight="bold"
                                                sx={{ color: COLORS.text }}
                                            >
                                                {financeText("Payable Filters")}
                                            </MDTypography>

                                            <MDTypography
                                                variant="caption"
                                                sx={{ color: COLORS.subtext }}
                                            >
                                                {financeText(
                                                    "Search and segment supplier liabilities by payment, approval and due bucket."
                                                )}
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>

                                    <MDBox display="flex" flexWrap="wrap" gap={1}>
                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            size="small"
                                            onClick={fetchPayables}
                                            sx={{ borderRadius: "10px" }}
                                        >
                                            <Refresh sx={{ mr: 0.5 }} />
                                            {financeText("Refresh")}
                                        </MDButton>

                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            size="small"
                                            onClick={resetFilters}
                                            sx={{ borderRadius: "10px" }}
                                        >
                                            <RestartAlt sx={{ mr: 0.5 }} />
                                            {financeText("Reset")}
                                        </MDButton>

                                        <MDButton
                                            variant="contained"
                                            size="small"
                                            onClick={exportCsv}
                                            sx={{
                                                borderRadius: "10px",
                                                background: `${COLORS.navy} !important`,
                                                color: "#FFFFFF !important",
                                                boxShadow: "none",
                                            }}
                                        >
                                            <Download sx={{ mr: 0.5 }} />
                                            {financeText("Export CSV")}
                                        </MDButton>

                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            size="small"
                                            onClick={() =>
                                                navigate("/invoice/finance/supplier-invoices")
                                            }
                                            sx={{ borderRadius: "10px" }}
                                        >
                                            {financeText("Supplier Invoices")}
                                        </MDButton>
                                    </MDBox>
                                </MDBox>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} lg={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={financeText("Search")}
                                            placeholder={financeText("Shipment, invoice, supplier...")}
                                            value={search}
                                            onChange={(event) => setSearch(event.target.value)}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Search
                                                            fontSize="small"
                                                            sx={{ color: COLORS.subtext }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Payment Status")}
                                            value={paymentStatus}
                                            onChange={(event) => setPaymentStatus(event.target.value)}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            {statusOptions.payment.map((item) => (
                                                <MenuItem key={item} value={item}>
                                                    {item === "all" ? financeText("All Payment Statuses") : financeText(item)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Approval Status")}
                                            value={approvalStatus}
                                            onChange={(event) =>
                                                setApprovalStatus(event.target.value)
                                            }
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            {statusOptions.approval.map((item) => (
                                                <MenuItem key={item} value={item}>
                                                    {item === "all" ? financeText("All Approval Statuses") : financeText(item)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Due Bucket")}
                                            value={dueBucket}
                                            onChange={(event) => setDueBucket(event.target.value)}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            <MenuItem value="all">{financeText("All Due Buckets")}</MenuItem>
                                            <MenuItem value="Overdue">{financeText("Overdue")}</MenuItem>
                                            <MenuItem value="Due Today">{financeText("Due Today")}</MenuItem>
                                            <MenuItem value="This Week">{financeText("This Week")}</MenuItem>
                                            <MenuItem value="This Month">{financeText("This Month")}</MenuItem>
                                            <MenuItem value="Later">{financeText("Later")}</MenuItem>
                                            <MenuItem value="No Due Date">{financeText("No Due Date")}</MenuItem>
                                            <MenuItem value="Closed">{financeText("Closed")}</MenuItem>
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Return Status")}
                                            value={returnStatus}
                                            onChange={(event) => setReturnStatus(event.target.value)}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            {returnStatusOptions.map((status) => (
                                                <MenuItem key={status} value={status}>
                                                    {status === "all" ? financeText("All Return Statuses") : financeText(status)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                </Grid>

                                <MDBox
                                    mt={2.2}
                                    p={1.5}
                                    borderRadius="8px"
                                    display="flex"
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    justifyContent="space-between"
                                    flexDirection={{ xs: "column", md: "row" }}
                                    gap={1}
                                    sx={{
                                        background: "#F9FAFB",
                                        border: `1px solid ${COLORS.border}`,
                                    }}
                                >
                                    <MDTypography
                                        variant="caption"
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {financeText("Showing")}{" "}
                                        <strong style={{ color: COLORS.text }}>
                                            {filteredPayables.length}
                                        </strong>{" "}
                                        {financeText("of")}{" "}
                                        <strong style={{ color: COLORS.text }}>
                                            {payables.length}
                                        </strong>{" "}
                                        {financeText("payable records")}
                                    </MDTypography>

                                    <MDBox
                                        display="flex"
                                        alignItems="center"
                                        flexWrap="wrap"
                                        gap={1}
                                    >
                                        <Chip
                                            size="small"
                                            label={`${financeText("Master Cost")} ${formatCompact(stats.masterCost, selectedCurrency)}`}
                                            sx={{
                                                color: COLORS.blue,
                                                background: COLORS.blueLight,
                                                fontWeight: 700,
                                            }}
                                        />

                                        <Chip
                                            size="small"
                                            label={`${financeText("Additional")} ${formatCompact(
                                                stats.additionalCost,
                                                selectedCurrency,
                                            )}`}
                                            sx={{
                                                color: COLORS.purple,
                                                background: COLORS.purpleLight,
                                                fontWeight: 700,
                                            }}
                                        />

                                        <Chip
                                            size="small"
                                            label={`${financeText("Returns")} ${formatCompact(stats.returnedAmount, selectedCurrency)}`}
                                            sx={{
                                                color: COLORS.purple,
                                                background: COLORS.purpleLight,
                                                fontWeight: 700,
                                            }}
                                        />

                                        {stats.supplierCredit > 0 && (
                                            <Chip
                                                size="small"
                                                label={`${financeText("Supplier Credit")} ${formatCompact(stats.supplierCredit, selectedCurrency)}`}
                                                sx={{
                                                    color: COLORS.purple,
                                                    background: COLORS.purpleLight,
                                                    fontWeight: 700,
                                                }}
                                            />
                                        )}

                                        <Chip
                                            size="small"
                                            label={`${financeText("Open")} ${formatCompact(stats.openPayable, selectedCurrency)}`}
                                            sx={{
                                                color: COLORS.amber,
                                                background: COLORS.amberLight,
                                                fontWeight: 700,
                                            }}
                                        />

                                        <Chip
                                            size="small"
                                            label={`${financeText("Overdue")} ${formatCompact(stats.overdueAmount, selectedCurrency)}`}
                                            sx={{
                                                color: COLORS.red,
                                                background: COLORS.redLight,
                                                fontWeight: 700,
                                            }}
                                        />
                                    </MDBox>
                                </MDBox>

                                <Divider sx={{ my: 2.5 }} />

                                <MDBox
                                    sx={{
                                        "& .MuiTableContainer-root": {
                                            border: `1px solid ${COLORS.border}`,
                                            borderRadius: "10px",
                                            overflowX: "auto",
                                            boxShadow: "none",
                                        },
                                        "& table": {
                                            minWidth: 1450,
                                            borderCollapse: "separate",
                                            borderSpacing: 0,
                                        },
                                        "& table thead": {
                                            background:
                                                "#F8FAFC",
                                        },
                                        "& table thead th": {
                                            background: "transparent",
                                            color: "#475467 !important",
                                            fontSize: "10px !important",
                                            fontWeight: "800 !important",
                                            letterSpacing: ".085em",
                                            paddingTop: "15px !important",
                                            paddingBottom: "15px !important",
                                            borderBottom: `1px solid ${COLORS.border} !important`,
                                        },
                                        "& table tbody td": {
                                            paddingTop: "14px !important",
                                            paddingBottom: "14px !important",
                                            borderBottom: "1px solid #F0F2F5 !important",
                                            verticalAlign: "middle",
                                        },
                                        "& table tbody tr": {
                                            background: "#FFFFFF",
                                            transition: "background .18s ease, box-shadow .18s ease",
                                        },
                                        "& table tbody tr:nth-of-type(even)": {
                                            background: "#FCFCFD",
                                        },
                                        "& table tbody tr:hover": {
                                            background: "#F5F8FF !important",
                                            boxShadow: "inset 3px 0 0 #175CD3",
                                        },
                                        "& table tbody tr:last-child td": {
                                            borderBottom: "0 !important",
                                        },
                                        "& .MuiTablePagination-root": {
                                            borderTop: `1px solid ${COLORS.border}`,
                                            background: "#FCFCFD",
                                        },
                                    }}
                                >
                                    <DataTable
                                        table={tableData}
                                        canSearch={false}
                                        entriesPerPage
                                        showTotalEntries
                                        isSorted
                                        noEndBorder
                                    />
                                </MDBox>
                            </MDBox>
                        </Card>

                        {/* İkincil grafik ve kırılımlar: varsayılan kapalı, üstteki filtrelere bağlı */}
                        <FinanceDetailsSection
                            storageKey="payables"
                            title={financeText("Detailed analysis")}
                            subtitle={financeText("Charts and breakdowns for the current filters")}
                        >
                            <Grid container spacing={SPACE.rowGap}>
                            <Grid item xs={12} lg={7}>
                                <FinanceHeroStat
                                    label={financeText("Net Payable")}
                                    value={formatCompact(stats.totalPayable, selectedCurrency)}
                                    tone="red"
                                    icon={<AccountBalanceWallet />}
                                    meta={`${financeText("Gross")} ${formatCompact(stats.grossPayable, selectedCurrency)} · ${financeText("Returns")} ${formatCompact(stats.returnedAmount, selectedCurrency)}`}
                                    context={[
                                        { label: financeText("Paid"), value: formatCompact(stats.paidAmount, selectedCurrency) },
                                        { label: financeText("Open Payable"), value: formatCompact(stats.openPayable, selectedCurrency) },
                                        { label: financeText("Master Cost"), value: formatCompact(stats.masterCost, selectedCurrency) },
                                    ]}
                                />
                            </Grid>

                            <Grid item xs={12} lg={5}>
                                <FinanceCard>
                                    <MDBox p={SPACE.cardPaddingLg} display="flex" flexDirection="column" height="100%">
                                        <FinanceSectionHeader
                                            title={getFinanceLanguage() === "tr" ? "Dikkat gerektiren" : "Needs attention"}
                                        />
                                        <MDBox display="flex" flexDirection="column" gap={1.25}>
                                            {[
                                                { label: financeText("Overdue"), value: formatCompact(stats.overdueAmount, selectedCurrency), sub: financeText("Past due supplier debt"), tone: "red" },
                                                { label: financeText("Additional Cost"), value: formatCompact(stats.additionalCost, selectedCurrency), sub: `${stats.includedInMasterCount} ${financeText("records already included")}`, tone: "purple" },
                                                { label: financeText("Critical"), value: formatNumber(stats.criticalCount), sub: financeText("High-priority suppliers"), tone: "red" },
                                            ].map((row) => (
                                                <MDBox
                                                    key={row.label}
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    px={1.75}
                                                    py={1.25}
                                                    borderRadius={SPACE.radiusTile}
                                                    sx={{ background: COLORS.muted }}
                                                >
                                                    <MDBox display="flex" alignItems="center" gap={1.5} minWidth={0}>
                                                        <MDBox width={8} height={8} borderRadius="50%" flexShrink={0} sx={{ background: COLORS[row.tone] }} />
                                                        <MDBox minWidth={0}>
                                                            <MDTypography variant="button" fontWeight="medium" sx={{ color: COLORS.text, display: "block" }}>
                                                                {row.label}
                                                            </MDTypography>
                                                            <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                                                                {row.sub}
                                                            </MDTypography>
                                                        </MDBox>
                                                    </MDBox>
                                                    <MDTypography variant="button" fontWeight="bold" sx={{ color: COLORS[row.tone], flexShrink: 0 }}>
                                                        {row.value}
                                                    </MDTypography>
                                                </MDBox>
                                            ))}
                                        </MDBox>
                                    </MDBox>
                                </FinanceCard>
                            </Grid>
                        </Grid>

                            <Grid container spacing={2.5} mt={0}>
                            {Object.entries(transportSummary).map(([mode, data]) => (
                                <Grid item xs={12} sm={6} lg={3} key={financeText(mode)}>
                                    <TransportModeCard
                                        mode={financeText(mode)}
                                        data={data}
                                        currency={selectedCurrency}
                                    />
                                </Grid>
                            ))}
                        </Grid>

                            <Grid container spacing={2.5} mt={0}>
                            <Grid item xs={12} lg={7}>
                                <ModeComparison
                                    summary={transportSummary}
                                    currency={selectedCurrency}
                                />
                            </Grid>
                            <Grid item xs={12} lg={5}>
                                <Card sx={{ ...cardSx, height: "100%" }}>
                                    <MDBox p={2.5}>
                                        <MDBox display="flex" alignItems="center" gap={1}>
                                            <MDBox
                                                width={38}
                                                height={38}
                                                borderRadius="11px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                sx={{
                                                    color: COLORS.purple,
                                                    background: COLORS.purpleLight,
                                                    border: "1px solid #D9D6FE",
                                                }}
                                            >
                                                <Inventory2 sx={{ fontSize: 19 }} />
                                            </MDBox>
                                            <MDBox>
                                                <MDTypography
                                                    variant="h6"
                                                    fontWeight="bold"
                                                    sx={{ color: COLORS.text }}
                                                >
                                                    {financeText("Shipment Type Mix")}
                                                </MDTypography>
                                                <MDTypography
                                                    variant="caption"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText("Open payables by service configuration.")}
                                                </MDTypography>
                                            </MDBox>
                                        </MDBox>
                                        <MDBox mt={2}>
                                            {shipmentTypeSummary.length ? (
                                                shipmentTypeSummary.slice(0, 6).map(([type, data]) => (
                                                    <MDBox
                                                        key={type}
                                                        display="flex"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        py={0.8}
                                                        borderBottom="1px solid #F2F4F7"
                                                    >
                                                        <MDBox>
                                                            <MDTypography
                                                                variant="button"
                                                                fontWeight="bold"
                                                                sx={{ color: COLORS.text }}
                                                            >
                                                                {type}
                                                            </MDTypography>
                                                            <MDTypography
                                                                variant="caption"
                                                                display="block"
                                                                sx={{ color: COLORS.subtext }}
                                                            >
                                                                {data.count} {financeText("records")}
                                                            </MDTypography>
                                                        </MDBox>
                                                        <MDTypography
                                                            variant="button"
                                                            fontWeight="bold"
                                                            sx={{ color: COLORS.purple }}
                                                        >
                                                            {formatCompact(data.open, selectedCurrency)}
                                                        </MDTypography>
                                                    </MDBox>
                                                ))
                                            ) : (
                                                <MDTypography
                                                    variant="caption"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText("No shipment type data available.")}
                                                </MDTypography>
                                            )}
                                        </MDBox>
                                    </MDBox>
                                </Card>
                            </Grid>
                        </Grid>

                            <Card sx={{ ...cardSx, mt: 2.5 }}>
                            <MDBox p={3}>
                                <MDBox
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    flexDirection={{ xs: "column", md: "row" }}
                                    gap={1.5}
                                    mb={2.5}
                                >
                                    <MDBox>
                                        <MDTypography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{ color: COLORS.text }}
                                        >
                                            {financeText("Payment Schedule")}
                                        </MDTypography>

                                        <MDTypography
                                            variant="caption"
                                            sx={{ color: COLORS.subtext }}
                                        >
                                            {financeText("Open supplier balances grouped by maturity.")}
                                        </MDTypography>
                                    </MDBox>

                                    <Chip
                                        size="small"
                                        label={`${financeText("Open")} ${formatCompact(stats.openPayable, selectedCurrency)}`}
                                        sx={{
                                            color: COLORS.amber,
                                            background: COLORS.amberLight,
                                            fontWeight: 700,
                                        }}
                                    />
                                </MDBox>

                                <Grid container spacing={2}>
                                    {Object.entries(stats.buckets).map(([bucket, amount]) => {
                                        const percent = stats.openPayable
                                            ? Math.min((amount / stats.openPayable) * 100, 100)
                                            : 0;

                                        const meta = bucketMeta[bucket];

                                        return (
                                            <Grid item xs={12} sm={6} lg={2} key={financeText(bucket)}>
                                                <Card
                                                    sx={{
                                                        borderRadius: "15px",
                                                        border: `1px solid ${COLORS.border}`,
                                                        boxShadow: "none",
                                                        height: "100%",
                                                        transition:
                                                            "transform .2s ease, border-color .2s ease",
                                                        "&:hover": {
                                                            transform: "none",
                                                            borderColor: meta.color,
                                                        },
                                                    }}
                                                >
                                                    <MDBox p={2}>
                                                        <MDBox
                                                            display="flex"
                                                            justifyContent="space-between"
                                                            alignItems="center"
                                                            mb={1}
                                                        >
                                                            <MDTypography
                                                                variant="button"
                                                                fontWeight="bold"
                                                                sx={{ color: COLORS.text }}
                                                            >
                                                                {financeText(bucket)}
                                                            </MDTypography>

                                                            <Chip
                                                                size="small"
                                                                label={`${percent.toFixed(1)}%`}
                                                                sx={{
                                                                    height: 22,
                                                                    color: meta.color,
                                                                    background: meta.background,
                                                                    fontWeight: 700,
                                                                }}
                                                            />
                                                        </MDBox>

                                                        <MDTypography
                                                            variant="h5"
                                                            fontWeight="bold"
                                                            sx={{ color: meta.color }}
                                                        >
                                                            {/* stats.buckets is already
                                                                converted into the selected
                                                                display currency; formatting it
                                                                without that currency used to
                                                                label e.g. a TRY total with "€". */}
                                                            {formatCompact(amount, selectedCurrency)}
                                                        </MDTypography>

                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={percent}
                                                            sx={{
                                                                height: 7,
                                                                borderRadius: "999px",
                                                                mt: 1.4,
                                                                backgroundColor: "#F2F4F7",
                                                                "& .MuiLinearProgress-bar": {
                                                                    borderRadius: "999px",
                                                                    background: meta.color,
                                                                },
                                                            }}
                                                        />
                                                    </MDBox>
                                                </Card>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </MDBox>
                        </Card>
                        </FinanceDetailsSection>

                    </>
                )}
            </MDBox>

            <Footer />
        </DashboardLayout>
    );
};

export default Payables;